import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, LabelList,
} from 'recharts';
import api from '../services/api';
import './ReportingAPV.css';

// ── Utils ────────────────────────────────────────────────────────────────────

const SEGMENTS = [
  { id: 'all',     label: 'Tout',      color: 'var(--color-accent)'  },
  { id: 'mo',      label: 'MO',        color: 'var(--color-warning)' },
  { id: 'pieces',  label: 'Pièces',    color: 'var(--color-info)'    },
  { id: 'travext', label: 'Trav. Ext.', color: 'var(--color-success)' },
  { id: 'autre',   label: 'Autre',     color: 'var(--text-muted)'    },
];

const SEGMENT_SERIES = [
  { key: 'ca_mo',      name: 'MO',         color: '#f59e0b' },
  { key: 'ca_pieces',  name: 'Pièces',     color: '#3b82f6' },
  { key: 'ca_travext', name: 'Trav. Ext.', color: '#22c55e' },
  { key: 'ca_autre',   name: 'Autre',      color: '#94a3b8' },
];

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case '7j': {
      const debut = new Date(today); debut.setDate(debut.getDate() - 6);
      return { debut: toISODate(debut), fin: toISODate(today) };
    }
    case '30j': {
      const debut = new Date(today); debut.setDate(debut.getDate() - 29);
      return { debut: toISODate(debut), fin: toISODate(today) };
    }
    case '90j': {
      const debut = new Date(today); debut.setDate(debut.getDate() - 89);
      return { debut: toISODate(debut), fin: toISODate(today) };
    }
    case 'mois-en-cours': {
      const debut = new Date(today.getFullYear(), today.getMonth(), 1);
      return { debut: toISODate(debut), fin: toISODate(today) };
    }
    case 'mois-dernier': {
      const debut = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const fin = new Date(today.getFullYear(), today.getMonth(), 0);
      return { debut: toISODate(debut), fin: toISODate(fin) };
    }
    case 'trimestre': {
      const q = Math.floor(today.getMonth() / 3);
      const debut = new Date(today.getFullYear(), q * 3, 1);
      return { debut: toISODate(debut), fin: toISODate(today) };
    }
    case 'annee': {
      const debut = new Date(today.getFullYear(), 0, 1);
      return { debut: toISODate(debut), fin: toISODate(today) };
    }
    default:
      return { debut: null, fin: null };
  }
}

function formatEur(n, compact = false) {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (compact && abs >= 1e6) return (n / 1e6).toFixed(1).replace('.', ',') + ' M€';
  if (compact && abs >= 1e3) return Math.round(n / 1e3) + ' k€';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: abs >= 10000 ? 0 : 2,
  }).format(n);
}

function formatInt(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
}

function formatBucket(iso, granularite) {
  if (!iso) return '';
  const d = new Date(iso);
  if (granularite === 'month') {
    return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  }
  if (granularite === 'week') {
    return 'sem. ' + d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  }
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

// ── SegmentToggle ────────────────────────────────────────────────────────────

function SegmentToggle({ value, onChange }) {
  return (
    <div className="reporting-segment" role="group" aria-label="Segment CA">
      {SEGMENTS.map(s => (
        <button
          key={s.id}
          type="button"
          className={value === s.id ? 'active' : ''}
          onClick={() => onChange(s.id)}
          title={s.id === 'autre' ? 'Lignes saisies hors liste (à requalifier)' : `CA ${s.label}`}
        >
          {s.id === 'autre' && <span className="warn-dot" />}
          {s.label}
        </button>
      ))}
    </div>
  );
}

// ── KPI cards ────────────────────────────────────────────────────────────────

function KpiRow({ kpis }) {
  if (!kpis) return null;
  const delta = kpis.delta_pct;
  const deltaClass = delta == null ? 'delta-neutral' : delta >= 0 ? 'delta-pos' : 'delta-neg';
  const deltaArrow = delta == null ? '—' : delta >= 0 ? '↗' : '↘';
  const deltaTxt = delta == null
    ? 'Pas de comparable'
    : `${deltaArrow} ${delta >= 0 ? '+' : ''}${delta.toFixed(1).replace('.', ',')} %`;

  return (
    <div className="reporting-kpi-grid">
      <div className="reporting-kpi-card">
        <div className="label">CA HT</div>
        <div className="value">{formatEur(kpis.ca_ht, true)}</div>
        <div className="sub">{formatEur(kpis.ca_ht)}</div>
      </div>
      <div className="reporting-kpi-card">
        <div className="label">Nombre de factures</div>
        <div className="value">{formatInt(kpis.nb_factures)}</div>
        <div className="sub">sur la période</div>
      </div>
      <div className="reporting-kpi-card">
        <div className="label">Ticket moyen</div>
        <div className="value">{formatEur(kpis.ticket_moyen)}</div>
        <div className="sub">CA / nb factures</div>
      </div>
      <div className="reporting-kpi-card">
        <div className="label">Évolution</div>
        <div className={`value ${deltaClass}`}>{deltaTxt}</div>
        <div className="sub">
          vs {formatEur(kpis.ca_precedent, true)} période précédente
        </div>
      </div>
    </div>
  );
}

// ── Timeseries ───────────────────────────────────────────────────────────────

function TimeseriesChart({ data, segment }) {
  const granularite = data?.granularite || 'day';
  const points = useMemo(
    () => (data?.points || []).map(p => ({ ...p, bucketLabel: formatBucket(p.bucket, granularite) })),
    [data, granularite],
  );

  if (!points.length) {
    return <div className="reporting-empty">Aucune donnée sur cette période.</div>;
  }

  const visibleSeries = segment === 'all'
    ? SEGMENT_SERIES
    : SEGMENT_SERIES.filter(s => s.key === `ca_${segment === 'travext' ? 'travext' : segment === 'autre' ? 'autre' : segment}`);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const total = segment === 'all'
      ? payload.find(p => p.dataKey === 'ca_total')?.value
      : null;
    return (
      <div className="reporting-tooltip">
        <div className="tooltip-title">{label}</div>
        {payload.filter(p => p.dataKey !== 'ca_total').map(p => (
          <div className="tooltip-row" key={p.dataKey}>
            <span><span className="swatch" style={{ background: p.color }} />{p.name}</span>
            <span>{formatEur(p.value)}</span>
          </div>
        ))}
        {segment === 'all' && total != null && (
          <div className="tooltip-row" style={{ marginTop: 4, fontWeight: 600 }}>
            <span>Total</span>
            <span>{formatEur(total)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={points} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
        <XAxis dataKey="bucketLabel" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
        <YAxis
          tickFormatter={v => formatEur(v, true)}
          tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
          width={60}
        />
        <Tooltip content={<CustomTooltip />} />
        {visibleSeries.map(s => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
        {segment === 'all' && (
          <Line
            type="monotone"
            dataKey="ca_total"
            name="Total"
            stroke="#0891b2"
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Top N bar chart ──────────────────────────────────────────────────────────

function TopCard({ title, icon, items, loading }) {
  if (loading) return <div className="reporting-chart-card"><h3>{icon} {title}</h3><div className="reporting-empty">Chargement…</div></div>;
  if (!items || !items.length) {
    return <div className="reporting-chart-card"><h3>{icon} {title}</h3><div className="reporting-empty">Aucune donnée.</div></div>;
  }

  const data = items.map(it => ({ ...it, labelCourt: it.label.length > 28 ? it.label.slice(0, 26) + '…' : it.label }));
  const height = Math.max(200, items.length * 32 + 30);

  return (
    <div className="reporting-chart-card">
      <h3>{icon} {title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 70, left: 8, bottom: 0 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="labelCourt"
            width={140}
            tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
          />
          <Tooltip
            cursor={{ fill: 'var(--bg-hover)' }}
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const p = payload[0].payload;
              return (
                <div className="reporting-tooltip">
                  <div className="tooltip-title">{p.label}</div>
                  <div className="tooltip-row"><span>CA HT</span><span>{formatEur(p.ca_ht)}</span></div>
                  <div className="tooltip-row"><span>Factures</span><span>{formatInt(p.nb_factures)}</span></div>
                </div>
              );
            }}
          />
          <Bar dataKey="ca_ht" fill="var(--color-accent)" radius={[0, 4, 4, 0]}>
            <LabelList
              dataKey="ca_ht"
              position="right"
              formatter={v => formatEur(v, true)}
              style={{ fontSize: 11, fill: 'var(--text-primary)' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Panneau audit "Autre" ────────────────────────────────────────────────────

function AutrePanel({ items, total }) {
  if (!items || !items.length || !total) return null;
  return (
    <div className="reporting-autre-panel">
      <h4>⚠️ {formatEur(total, true)} de CA sur des libellés hors liste (à requalifier dans le DMS)</h4>
      <table>
        <thead>
          <tr>
            <th>Libellé saisi</th>
            <th style={{ textAlign: 'right' }}>CA HT</th>
            <th style={{ textAlign: 'right' }}>Lignes</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td>{it.libelle_prestation}</td>
              <td className="num">{formatEur(it.ca_ht)}</td>
              <td className="num">{formatInt(it.nb_lignes)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────

export default function ReportingAPV() {
  const [preset, setPreset]     = useState('30j');
  const [debut, setDebut]       = useState(null);
  const [fin, setFin]           = useState(null);
  const [segment, setSegment]   = useState('all');

  const [kpis, setKpis]               = useState(null);
  const [timeseries, setTimeseries]   = useState(null);
  const [topDept, setTopDept]         = useState([]);
  const [topMarque, setTopMarque]     = useState([]);
  const [topRecep, setTopRecep]       = useState([]);
  const [autreTop, setAutreTop]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  // Calcul de la plage effective depuis le preset
  const range = useMemo(() => {
    if (preset === 'custom') {
      return { debut, fin };
    }
    return computeRange(preset);
  }, [preset, debut, fin]);

  // Initialiser les date inputs au passage en mode custom
  useEffect(() => {
    if (preset === 'custom' && (!debut || !fin)) {
      const r = computeRange('30j');
      setDebut(r.debut);
      setFin(r.fin);
    }
  }, [preset, debut, fin]);

  const fetchAll = useCallback(async () => {
    if (!range.debut || !range.fin) return;
    setLoading(true);
    setError(null);
    try {
      const common = { date_debut: range.debut, date_fin: range.fin, segment };
      const [rk, rt, rd, rm, rr, ra] = await Promise.all([
        api.get('/reporting-apv/kpis',       { params: common }),
        api.get('/reporting-apv/timeseries', { params: common }),
        api.get('/reporting-apv/top',        { params: { ...common, dimension: 'department' } }),
        api.get('/reporting-apv/top',        { params: { ...common, dimension: 'marque' } }),
        api.get('/reporting-apv/top',        { params: { ...common, dimension: 'receptionnaire' } }),
        api.get('/reporting-apv/autre-top',  { params: { date_debut: range.debut, date_fin: range.fin, limit: 5 } }),
      ]);
      setKpis(rk.data);
      setTimeseries(rt.data);
      setTopDept(rd.data.items || []);
      setTopMarque(rm.data.items || []);
      setTopRecep(rr.data.items || []);
      setAutreTop(ra.data.items || []);
    } catch (err) {
      console.error('Erreur reporting APV:', err);
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [range.debut, range.fin, segment]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePreset = (e) => {
    const v = e.target.value;
    setPreset(v);
    if (v !== 'custom') {
      const r = computeRange(v);
      setDebut(r.debut);
      setFin(r.fin);
    }
  };

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className="active">📊 Reporting APV</button>
      </div>

      <div className="page-toolbar">
        <select
          className={`page-select ${preset !== '30j' ? 'active-filter' : ''}`}
          value={preset}
          onChange={handlePreset}
          aria-label="Période"
        >
          <option value="7j">7 derniers jours</option>
          <option value="30j">30 derniers jours</option>
          <option value="90j">90 derniers jours</option>
          <option value="mois-en-cours">Mois en cours</option>
          <option value="mois-dernier">Mois dernier</option>
          <option value="trimestre">Trimestre en cours</option>
          <option value="annee">Année en cours</option>
          <option value="custom">Personnalisé…</option>
        </select>

        {preset === 'custom' && (
          <>
            <input
              type="date"
              className="page-select"
              value={debut || ''}
              onChange={e => setDebut(e.target.value)}
              aria-label="Date début"
            />
            <input
              type="date"
              className="page-select"
              value={fin || ''}
              onChange={e => setFin(e.target.value)}
              aria-label="Date fin"
            />
          </>
        )}

        <SegmentToggle value={segment} onChange={setSegment} />

        {loading && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            ⏳ Chargement…
          </span>
        )}
      </div>

      {error && (
        <div style={{
          padding: '10px 14px',
          background: 'var(--color-error-light)',
          color: 'var(--color-error-dark)',
          borderRadius: 8, fontSize: 13,
        }}>
          {error}
        </div>
      )}

      <KpiRow kpis={kpis} />

      <div className="reporting-chart-card">
        <h3>📈 Évolution du CA {timeseries?.granularite && `(par ${
          { day: 'jour', week: 'semaine', month: 'mois' }[timeseries.granularite]
        })`}</h3>
        <TimeseriesChart data={timeseries} segment={segment} />
      </div>

      <div className="reporting-topn-grid">
        <TopCard title="Top départements" icon="🏢" items={topDept} loading={loading && !topDept.length} />
        <TopCard title="Top marques"      icon="🚗" items={topMarque} loading={loading && !topMarque.length} />
      </div>

      <TopCard title="Top réceptionnaires" icon="👤" items={topRecep} loading={loading && !topRecep.length} />

      {segment === 'all' && kpis && kpis.repartition && kpis.repartition.autre > 0 && (
        <AutrePanel items={autreTop} total={kpis.repartition.autre} />
      )}

      <div className="reporting-footer">
        <span>
          {kpis?.nb_non_dates > 0 && (
            <>🕓 {formatInt(kpis.nb_non_dates)} facture(s) sans date de facturation exclues du reporting</>
          )}
        </span>
        <span>
          {range.debut && range.fin && (
            <>Période : {range.debut} → {range.fin}</>
          )}
        </span>
      </div>
    </div>
  );
}
